import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import ChartProvider from '../../../../../charts/echarts/ChartProvider'
import ChartColorProvider from '../../../../../charts/echarts/ColorProvider'
import Tooltip from '../../../../../charts/echarts/Tooltip'
import AxisX from '../../../../../charts/echarts/AxisX'
import AxisY from '../../../../../charts/echarts/AxisY'
import ComponentBar from '../../../../../charts/echarts/ComponentBar'
import { useFormattingContext } from '../../../../FormattingProvider/FormattingContext'
import { buildSliceTooltipFormatter } from './allocationTooltip'

// Horizontal-bar variant of the allocation chart. One bar per slice,
// sorted by abs desc (axis is inverted so the largest bar sits at
// the top).
//
// Axis layout for horizontal bars in ECharts is "category Y, value X":
// the value axis runs along the bottom (where bar length is measured)
// and the category axis runs up the left side (where bar names sit).
// `inverse: true` on the category axis flips the ordering so the
// first item in the data array (largest, since slices are pre-sorted)
// renders at the top instead of the bottom.
//
// Negative-value semantics match the donut: bar widths use `abs`,
// signed values appear in the tooltip via the shared formatter, and
// the detail table below still shows actual signed values.

export default function AllocationBarChart ({ slices, valueFormat, height = 340 }) {
  const { formatter: fmt } = useFormattingContext()
  const tooltipFormatter = useMemo(
    () => buildSliceTooltipFormatter(fmt, valueFormat),
    [fmt, valueFormat]
  )

  // Categories array in slice order (largest first). The axis is
  // inverted, so the visual order is largest-on-top.
  const categories = useMemo(() => slices.map(s => s.name), [slices])

  // Series data with raw + percent threaded through so the shared
  // tooltip formatter can read them. `colorBy: 'data'` on the bar
  // series makes ECharts assign a palette color per data item rather
  // than reusing one series-wide color -- that keeps the visual
  // identity of "Cash Savings" consistent with the donut.
  const data = useMemo(() => slices.map(s => ({
    // `id` carries the row's stable groupId so ECharts preserves
    // animation identity even when two rows share a display `name`.
    id: s.groupId != null ? String(s.groupId) : s.name,
    name: s.name,
    value: s.abs,
    raw: s.raw,
    percent: s.percent
  })), [slices])

  return (
    <ChartColorProvider theme='default'>
      <ChartProvider height={height} loadingTemplate='axis' emptyTemplate='axis'>
        <Tooltip trigger='item' formatter={tooltipFormatter} />
        <AxisX
          type='value'
          format={valueFormat}
          axisLabel={{ hideOverlap: true, fontSize: 10 }}
          splitLine={{ lineStyle: { color: 'var(--opal-border-default, #eee)' } }}
        />
        <AxisY
          type='category'
          data={categories}
          inverse
          // AxisY defaults `format='date'` and wraps axisLabel.formatter
          // with the formatting context unconditionally -- categories
          // like 'Cash Savings' would otherwise come out as "Invalid
          // Date". Passing format=null short-circuits the wrapper
          // (FormattingContext's formatValue returns value as-is when
          // formatString is falsy).
          format={null}
          axisLabel={{
            fontSize: 11,
            width: 120,
            overflow: 'truncate'
          }}
          axisTick={{ show: false }}
        />
        <ComponentBar
          seriesId='allocation'
          data={data}
          colorBy='data'
          barCategoryGap='30%'
          barMinHeight={4}
          label={{ show: false }}
        />
      </ChartProvider>
    </ChartColorProvider>
  )
}

AllocationBarChart.propTypes = {
  slices: PropTypes.arrayOf(PropTypes.shape({
    groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string.isRequired,
    raw: PropTypes.number.isRequired,
    abs: PropTypes.number.isRequired,
    percent: PropTypes.number.isRequired
  })).isRequired,
  valueFormat: PropTypes.string,
  height: PropTypes.number
}
